import { useCallback, useMemo } from 'react';

import dayjs from 'dayjs';

import { tocCountdownEndDelayMs } from '../../../stores/tournament/constants';
import { EnumOpenDialog } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';

interface ChampionsTournamentTimerProps {
  startDate: string;
  endDate: string;
  to: 'start' | 'end';
}

export function useChampionsTournamentWidgetData() {
  const { tournamentStore, tournamentOfChampionsStore, uiStore, coinTypeStore } = useStores();
  const {
    tournamentOfChampions,
    isActiveRound,
    isCountdownToNextRound,
    isInCountdownState,
    countdownEndDate,
    widgetIcon,
    currentRound,
  } = tournamentOfChampionsStore;

  const coinTypeLower = coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
  const tournament = tournamentStore.currentTournament;

  const currentRoundPlayerPosition = currentRound?.playerPosition ?? null;
  const tournamentPlayerPosition = tournament?.leaderboards[coinTypeLower]?.player?.position ?? null;
  const maxPositionDisplay = tournament?.tournamentGroup.maxPositionDisplay ?? 100;

  const positionValue = useMemo((): string | null => {
    if (!isActiveRound) return null;

    let playerPosition: number | null = currentRoundPlayerPosition != null ? currentRoundPlayerPosition + 1 : null;

    if (playerPosition == null && tournamentPlayerPosition != null && tournamentPlayerPosition >= 0) {
      playerPosition = tournamentPlayerPosition + 1;
    }

    if (playerPosition == null || playerPosition <= 0) return null;
    return playerPosition > maxPositionDisplay ? `${maxPositionDisplay}+` : `#${playerPosition}`;
  }, [currentRoundPlayerPosition, isActiveRound, maxPositionDisplay, tournamentPlayerPosition]);

  const currentRoundStartDate = currentRound?.startDate ?? null;
  const currentRoundEndDate = currentRound?.endDate ?? null;

  const timer = useMemo((): ChampionsTournamentTimerProps | null => {
    if (isCountdownToNextRound && countdownEndDate) {
      return { startDate: countdownEndDate, endDate: countdownEndDate, to: 'start' };
    }
    if (isActiveRound && currentRoundStartDate && currentRoundEndDate) {
      if (!dayjs(currentRoundEndDate).isAfter(dayjs())) return null;
      return { startDate: currentRoundStartDate, endDate: currentRoundEndDate, to: 'end' };
    }
    return null;
  }, [countdownEndDate, currentRoundEndDate, currentRoundStartDate, isActiveRound, isCountdownToNextRound]);

  const onTimerEnd = useCallback(() => {
    setTimeout(() => void tournamentOfChampionsStore.refreshStaleData(), tocCountdownEndDelayMs());
  }, [tournamentOfChampionsStore]);

  const handleClick = useCallback(() => {
    tournamentStore.handleAnalytics({
      event_action: 'impression',
      params: { triggered_by: 'click', click_location: 'widget' },
    });

    void tournamentStore.fetch();

    uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenTournamentDialog,
      options: { shown_by: 'widget_click' },
    });
  }, [tournamentStore, uiStore]);

  return {
    hasToc: tournamentOfChampions != null,
    widgetImageUrl: widgetIcon ?? undefined,
    positionValue,
    timer,
    showStartsInLabel: isCountdownToNextRound,
    showEmptyPositionBar: isActiveRound && positionValue == null,
    isInCountdownState,
    handleClick,
    onTimerEnd,
  };
}
